"use client";
import { checkEmail } from "@/actions/auth";
import { useT } from "@/app/i18n/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/client/betterauth/auth-client";
import { normalizeEmail } from "@/utils/normalize-email";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

interface LoginEmailOtpProps {
  onSignedIn?: (email: string) => void;
}

const OTP_LENGTH = 6;

export const LoginEmailOtp = ({ onSignedIn }: LoginEmailOtpProps) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"request" | "verify">("request");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { t } = useT("signIn", {});
  const router = useRouter();

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setIsLoading(true);

    const normalizedEmail = normalizeEmail(email);
    setEmail(normalizedEmail);

    try {
      const result = await checkEmail(normalizedEmail);
      if ("error" in result) {
        setError(result.error as string);
        return;
      }

      const { error: sendError } =
        await authClient.emailOtp.sendVerificationOtp({
          email: normalizedEmail,
          type: "sign-in",
        });

      if (sendError) {
        setError(t("errors.emailOtpError"));
        return;
      }

      setStep("verify");
      setCountdown(30);
      setCanResend(false);
    } catch (err) {
      console.error("Error sending email OTP:", err);
      setError(t("errors.emailOtpError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setIsLoading(true);

    const normalizedEmail = normalizeEmail(email);

    try {
      const { error: checkError } =
        await authClient.emailOtp.checkVerificationOtp({
          email: normalizedEmail,
          type: "sign-in",
          otp,
        });

      if (checkError) {
        const message = checkError.message || "";
        if (message.includes("OTP expired")) {
          setError(t("errors.otpExpired"));
        } else if (message.includes("Too many attempts")) {
          setError(t("errors.otpTooManyAttempts"));
        } else if (message.includes("Invalid OTP")) {
          setError(t("errors.otpInvalid"));
        } else {
          setError(t("errors.emailOtpError"));
        }
        return;
      }

      const { error: signInError } = await authClient.signIn.emailOtp({
        email: normalizedEmail,
        otp,
      });

      if (signInError) {
        const message = signInError.message || "";
        if (message.includes("OTP expired")) {
          setError(t("errors.otpExpired"));
        } else if (message.includes("Too many attempts")) {
          setError(t("errors.otpTooManyAttempts"));
        } else if (message.includes("Invalid OTP")) {
          setError(t("errors.otpInvalid"));
        } else if (message.includes("disabled")) {
          setError(t("errors.serverErrors.userdisabled"));
        } else if (message.includes("NoAccount")) {
          setError(t("errors.serverErrors.NoAccount"));
        } else {
          setError(t("errors.emailOtpError"));
        }
        return;
      }
      onSignedIn?.(normalizedEmail);
      router.push("/app");
    } catch (err) {
      console.error("Error verifying email OTP:", err);
      setError(t("errors.emailOtpError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep("request");
    setOtp("");
    setError(undefined);
  };

  const handleResendOtp = async () => {
    if (!canResend || isResending) return;
    setError(undefined);
    setIsResending(true);

    const normalizedEmail = normalizeEmail(email);

    try {
      // Vérifier le membre avant de renvoyer l'OTP
      const result = await checkEmail(normalizedEmail);
      if ("error" in result) {
        setError(result.error as string);
        return;
      }

      const { error: resendError } =
        await authClient.emailOtp.sendVerificationOtp({
          email: normalizedEmail,
          type: "sign-in",
        });

      if (resendError) {
        setError(t("errors.emailOtpError"));
        return;
      }

      setCountdown(30);
      setCanResend(false);
    } catch (err) {
      console.error("Error resending email OTP:", err);
      setError(t("errors.emailOtpError"));
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (step === "verify" && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (step === "verify" && countdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [step, countdown]);

  if (step === "verify") {
    return (
      <form onSubmit={handleVerifyOtp} className="space-y-3">
        <p className="text-center text-sm">
          {t("emailOtp.instructions1", { email })}
        </p>
        <p className="text-center text-sm">{t("emailOtp.instructions2")}</p>
        <div className="space-y-1">
          <div className="flex justify-center">
            <InputOTP
              id="otp"
              maxLength={OTP_LENGTH}
              value={otp}
              onChange={(value) => setOtp(value.replace(/\D/g, ""))}
              disabled={isLoading}
              inputMode="numeric"
              autoComplete="one-time-code"
            >
              <InputOTPGroup className="shadow-sm">
                {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
        <Button
          type="submit"
          className="h-10 w-full"
          disabled={isLoading || otp.length !== OTP_LENGTH}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("emailOtp.verifying")}
            </>
          ) : (
            t("emailOtp.verifyCta")
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full"
          onClick={handleResendOtp}
          disabled={!canResend || isResending}
        >
          {isResending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("emailOtp.resending")}
            </>
          ) : canResend ? (
            t("emailOtp.resendCode")
          ) : (
            `${t("emailOtp.resendIn")} ${countdown}s`
          )}
        </Button>
        {/* <Button
          type="button"
          variant="ghost"
          className="h-10 w-full"
          onClick={handleBack}
          disabled={isLoading}
        >
          {t("emailOtp.backToEmail")}
        </Button> */}
      </form>
    );
  }

  return (
    <form onSubmit={handleRequestOtp} className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="otp-email" className="text-sm">
          {t("emailOtp.emailAddress")}
        </Label>
        <Input
          id="otp-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("emailOtp.emailPlaceholder")}
          required
          disabled={isLoading}
          className="h-10"
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
      <Button type="submit" className="h-10 w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("emailOtp.sending")}
          </>
        ) : (
          t("emailOtp.sendCta")
        )}
      </Button>
    </form>
  );
};

export default LoginEmailOtp;
